import React, {FC, PropsWithChildren} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {MacroModel} from '../../../../models/api/macroModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {SettingsRuleResourcePagesEnum} from '../../../../models/url/models/rulesUrlModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {
  EducationTooltipCoordinator,
  EducationTooltipCoordinatorProps,
} from '../../../common/tooltips/educationTooltip/educationTooltipCoordinator';

/*
 * Props.
 */

export interface MacroTooltipCoordinatorProps
  extends Pick<EducationTooltipCoordinatorProps, 'customMargin' | 'placement'> {
  macro: MacroModel;
}

/*
 * Component.
 */

export const MacroTooltipCoordinator: FC<PropsWithChildren<MacroTooltipCoordinatorProps>> = (props) => {
  const {children, macro, customMargin, placement} = props;
  const {navigateToLink} = useLinkNavigation();

  const hasPermission = useAppSelector(getHasPermission);
  const canEditMacro = hasPermission(macro.namespace, PermissionsEnum.MACRO_UPDATE);

  return (
    <EducationTooltipCoordinator
      title={macro.name}
      tooltip={macro.description}
      buttons={
        canEditMacro
          ? [
              <Button size={VisualSizesEnum.SMALL} onClick={() => openInSettings(navigateToLink, macro)}>
                <Trans id="macro-trigger-tooltip-gotosettings">Edit macro</Trans>
              </Button>,
            ]
          : undefined
      }
      customMargin={customMargin ?? '0px 2px'}
      placement={placement}
    >
      {children}
    </EducationTooltipCoordinator>
  );
};

/*
 * Helpers.
 */

function openInSettings(navigate: (arg: string) => void, macro: MacroModel) {
  const editMacroUrl = buildSettingsPath({
    namespace: macro.namespace,
    macroId: macro.id,
    rootPage: SettingsRootPagesEnum.RULES,
    resourcePage: SettingsRuleResourcePagesEnum.MACROS,
    subResourcePage: SettingsResourcePagesEnum.EDIT,
  });
  return navigate(editMacroUrl);
}
